import Button from "@Components/Button";
import { notification } from "antd";
import classNames from "classnames";
import React, { ReactNode, RefObject, SetStateAction } from "react";
import { useDropzone } from "react-dropzone";
import type { Observable } from "rxjs";
import FileLoadedContent from "./FileLoadedContent/FileLoadedContent";
import FileLoadingContent from "./FileLoadingContent/FileLoadingContent";
import "./UploadFile.scss";
import { CloudUpload, UpFromBracket } from "assets/icons";
import { AxiosError } from "axios";

export interface FileModel {
  id?: number;
  fileId?: string | number;
  name?: string;
  url?: string;
  appUserId?: number;
  rowId?: string;
  content?: string;
  mimeType?: string;
  isFile?: boolean;
  key?: any;
  path?: string;
  level?: number;
  isDelete?: boolean;
  clearAction?: (fileId: string | number) => void;
  handleInput?: (e: any) => void;
}
export interface UploadFileProps {
  /**Option for multiple UploadFile */
  isMultiple?: boolean;
  /**Title button */
  titleButton?: string;
  /**Content of UploadFile component */
  uploadContent?: string;
  /**Content of UploadFile component */
  textHint?: string;
  /**Function change list file uploaded */
  updateList?: (files: FileModel[]) => void;
  /**API use to upload list file selected to server*/
  uploadFile?: (files: File[] | Blob[]) => Observable<FileModel[]>;
  /**Option limit maximum size (b) of list file to upload to server */
  maximumSize?: number;
  /** Option to change style of upload  "link", "button", "dragAndDrop"*/
  type?: "link" | "button" | "dragAndDrop";
  /**Icon of component */
  icon?: JSX.Element;
  /**Option to set component have only view mode */
  isViewMode?: boolean;
  /**Function to set list file in status loading */
  setListFileLoading?: (files: FileModel[]) => void;
  /**Use to custom style the component*/
  className?: string;
  /**acept file */
  acceptFiles?: string;
  /**Function to handle error when upload file */
  onUploadError?: (error: AxiosError<any>) => void;
  disabled?: boolean;
}
export function UploadFile(props: UploadFileProps) {
  const {
    uploadContent,
    isMultiple = true,
    updateList,
    uploadFile,
    maximumSize = 25000000,
    type = "dragAndDrop",
    icon,
    setListFileLoading,
    className,
    textHint,
    acceptFiles,
    titleButton = "Tải file",
    onUploadError,
    disabled,
  } = props;

  const fileRef: RefObject<HTMLInputElement> = React.useRef<HTMLInputElement>();

  const [isError, setIsError] = React.useState<boolean>(false);

  const handleClickButton = React.useCallback(() => {
    fileRef.current.click();
    fileRef.current.value = null;
  }, []);

  const handleValidateFile = React.useCallback(
    (files: File[]) => {
      let checkValidate = true;
      let totalSize = 0;
      files.forEach((file) => {
        totalSize = totalSize + file.size;
        if (totalSize > maximumSize) {
          notification.error({
            message: `Vượt quá dung lượng cho phép`,
            description: `File tải lên dung lượng phải dưới ${(
              maximumSize / 1000000
            ).toFixed(2)}MB`,
            placement: "bottomRight",
          });
          checkValidate = false;
        }
      });

      return { checkValidate };
    },
    [maximumSize]
  );

  const handleChangeFileInput = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files: File[] = [];
      Array.from(event?.target?.files).forEach((file) => {
        files.push(file);
      });
      const { checkValidate } = handleValidateFile(files);
      if (!checkValidate) {
        setIsError(true);
        return null;
      } else {
        setIsError(false);
      }
      if (typeof setListFileLoading == "function") {
        setListFileLoading([...files]);
      }
      if (files && files.length > 0) {
        uploadFile(files).subscribe({
          next: (res: FileModel[]) => {
            if (res && res.length > 0) {
              updateList([...res]);
              setListFileLoading([]);
              if (typeof setListFileLoading == "function") {
                setListFileLoading([]);
              }
            }
          },
          error: (error: AxiosError<any>) => {
            if (typeof setListFileLoading == "function") {
              setListFileLoading([]);
            }
            onUploadError?.(error);
          },
        });
      }
      // event.target.value = null;
    },
    [handleValidateFile, setListFileLoading, updateList, uploadFile]
  );

  const handleChangeFileDropzone = React.useCallback(
    (files: File[]) => {
      const { checkValidate } = handleValidateFile(files);
      if (!checkValidate) {
        setIsError(true);
        return null;
      } else {
        setIsError(false);
      }
      if (typeof setListFileLoading == "function") {
        setListFileLoading([...files]);
      }
      if (files && files.length > 0) {
        uploadFile(files).subscribe({
          next: (res: FileModel[]) => {
            if (res && res.length > 0) {
              updateList([...res]);
              if (typeof setListFileLoading == "function") {
                setListFileLoading([]);
              }
            }
          },
          error: (error: AxiosError<any>) => {
            if (typeof setListFileLoading == "function") {
              setListFileLoading([]);
            }
            onUploadError?.(error);
          },
        });
      }
    },
    [handleValidateFile, setListFileLoading, updateList, uploadFile]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleChangeFileDropzone,
    accept: acceptFiles,
    disabled,
  });

  React.useEffect(() => {
    if (type === "dragAndDrop") {
      const dropzone = document.getElementsByClassName("upload-dropzone")?.[0];
      if (dropzone) {
        setTimeout(() => {
          dropzone.addEventListener("dragenter", () => {
            dropzone.classList.add("is-active");
          });
          dropzone.addEventListener("dragleave", () => {
            dropzone.classList.remove("is-active");
          });
          dropzone.addEventListener("drop", () => {
            dropzone.classList.remove("is-active");
          });
        }, 200);
      }
    }

  });

  return (
    <div className={classNames("upload-button__container", className, {
      "disabled": disabled,
    })}>
      <div>
        {type === "dragAndDrop" ? (
          <div className={classNames("upload-dropzone")} {...getRootProps()}>
            {isError ? (
              <div className="error">Vượt quá kích thước</div>
            ) : (
              <>
                {icon ? icon : <CloudUpload />}
                <div className="button-upload-file m-t--2xs m-b--3xs">
                  <Button type="tertiary" size="lg" disabled={disabled}>
                    {titleButton}
                  </Button>
                </div>
                <div className="text-hint-upload">{textHint}</div>
              </>
            )}
            <input
              type="file"
              style={{ display: "none" }}
              className="input-dropzone"
              multiple={isMultiple}
              ref={fileRef}
              disabled={disabled}
              {...getInputProps()}
            />
          </div>
        ) : type === "link" ? (
          <div className="upload-link" onClick={handleClickButton}>
            {icon ? (
              icon
            ) : (
              <UpFromBracket fillColor="var(--palette-base-blue-5)" />
            )}
            <span className="upload-content m-l--2xs">{uploadContent}</span>
          </div>
        ) : (
          <div className="upload-button__list-file">
            <Button
              type="secondary"
              size="lg"
              icon={icon || <UpFromBracket />}
              iconPlace="left"
              onClick={handleClickButton}
              disabled={disabled}
            >
              {titleButton}
            </Button>
            <div className="text-hint-upload m-t--3xs">{textHint}</div>
          </div>
        )}

        <input
          type="file"
          style={{ display: "none" }}
          multiple={isMultiple}
          ref={fileRef}
          accept={acceptFiles}
          disabled={disabled}
          onChange={handleChangeFileInput}
        />
      </div>
    </div>
  );
}
UploadFile.FileLoadedContent = FileLoadedContent;
UploadFile.FileLoadingContent = FileLoadingContent;

export default UploadFile;
